import type { AppState } from '../core/types';
import { layerClassificationAssignments } from '../domain/classification';
import { SECONDARY_NO_VALUE } from '../domain/dualClassification';
import { deriveDisplaySet } from '../domain/displaySet';
import { measureArea, NICARAGUAN_MANZANA_M2 } from '../domain/measurement';
import { remainderIds, visibleThemeTargetIds } from '../domain/themeTarget';
import { entityViewRows } from '../domain/entityView';
import type { LegendConfig } from './config';
import { masterThemeAssignments } from './masterTheme';
import { secondaryTheme } from './dualTheme';
import { apportionRounded } from './areaRounding';
import { legendOrdered } from './legendDesign';
import { legendCrossTab } from './legendGroups';
import { scenarioMarkPlacement } from './scenarioActions';

export interface LegendCompositionRow {
  readonly value: string;
  readonly label: string;
  readonly color: string;
  readonly count: number;
  /** Area in the legend unit, full precision. */
  readonly area: number;
  /** The area as shown (apportioned with the other rows of its legend: see areaRounding). */
  readonly shownArea: number;
  readonly metrics: string;
  readonly remainder: boolean;
  /** Secondary legend: the neutral «Sin ‹B›» row (entities without a B value; no outline on the map). */
  readonly neutral?: boolean;
}

export interface LegendComposition {
  readonly config: LegendConfig;
  readonly layerName: string;
  readonly rows: readonly LegendCompositionRow[];
  /** V1.9.0: name of the proposal drawn (the legend says it is a scenario, not executed). */
  readonly scenario?: string;
  /** V1.8.1 secondary classification (outline): its own title, rows, metrics and line. Absent in the grouped legend. */
  readonly secondary?: SecondaryLegendComposition;
  /** V1.9.0 E3 grouped legend: B groups → A rows (then `rows` only keeps the «resto visible» row, if any). */
  readonly grouped?: GroupedLegendComposition;
}

export interface LegendGroup {
  readonly value: string;
  readonly label: string;
  /** B outline colour. */
  readonly color: string;
  /** Unique entities of the group (a split entity counts once). */
  readonly count: number;
  readonly area: number;
  readonly shownArea: number;
  /** Subtotal text ('' when subtotals are hidden). */
  readonly metrics: string;
  readonly rows: readonly LegendCompositionRow[];
  /** The neutral group of entities without a B value (no outline on the map: drawn as an empty swatch). */
  readonly neutral?: boolean;
}

export interface GroupedLegendComposition {
  /** Title of the grouping dimension (B), editable like in the flat legend. */
  readonly title: string;
  readonly line: 'solid' | 'dashed';
  readonly groups: readonly LegendGroup[];
  /**
   * Total line (absent when hidden): unique entities and area of what is shown. Labelled «Área mostrada» when hidden
   * groups or categories leave classified area out (never a silent «Total»).
   */
  readonly total?: { readonly label: string; readonly count: number; readonly area: number; readonly shownArea: number; readonly metrics: string };
  /** Classified area (legend unit) left out by hidden groups or categories: reported in the editor. */
  readonly excludedArea: number;
}

/** Label of the neutral group / row of entities without a secondary value (e.g. «Sin Hacienda»). */
export const unassignedLabel = (name: string): string => `Sin ${name.trim() || 'valor'}`;
const UNASSIGNED_COLOR = '#9aa5a0';
const EPSILON_AREA = 1e-9;

export interface SecondaryLegendComposition {
  readonly title: string;
  readonly rows: readonly LegendCompositionRow[];
  readonly line: 'solid' | 'dashed';
}

const toUnit = (legend: LegendConfig, hectares: number): number => legend.areaUnit === 'ha' ? hectares : hectares * 10_000 / NICARAGUAN_MANZANA_M2;
export const formatLegendArea = (legend: LegendConfig, area: number): string => `${area.toLocaleString('es-NI', { minimumFractionDigits: legend.decimals, maximumFractionDigits: legend.decimals })} ${legend.areaUnit}`;
const metricsText = (legend: LegendConfig, count: number, shownArea: number): string =>
  [legend.showCount ? `${count}` : '', legend.showArea ? formatLegendArea(legend, shownArea) : ''].filter(Boolean).join(' · ');

/** Rows of one legend (one column of areas): shown areas apportioned so they add up to the rounded total. */
function withMetrics<T extends { readonly count: number; readonly hectares: number }>(legend: LegendConfig, items: readonly T[]): (Omit<T, 'hectares'> & Pick<LegendCompositionRow, 'area' | 'shownArea' | 'metrics'>)[] {
  const areas = items.map(({ hectares }) => toUnit(legend, hectares));
  const shown = apportionRounded(areas, legend.decimals);
  return items.map(({ hectares: _hectares, ...item }, index) => { void _hectares; return { ...item, area: areas[index]!, shownArea: shown[index]!, metrics: metricsText(legend, item.count, shown[index]!) }; });
}

/** Shared semantic model for the on-screen legend and every cartographic output. */
export function buildLegendComposition(state: AppState): LegendComposition | null {
  const layer = state.layers.find(({ id }) => id === state.classificationConfig.layerId);
  const field = state.classificationConfig.field;
  const legend = state.classificationConfig.legend;
  if (!legend.visible || !layer || !field || !state.classificationConfig.categories.length) return null;
  const theme = layer.kind === 'master' ? masterThemeAssignments(state, layer) : null;
  const ids = theme?.subsetIds ?? visibleThemeTargetIds(state, layer);
  const assignments = theme?.assignments ?? layerClassificationAssignments(layer, field, ids, state.classificationConfig);
  const rows: LegendCompositionRow[] = withMetrics(legend, legendOrdered(state.classificationConfig.categories, legend.categoryOrder).flatMap((category) => {
    const count = assignments.counts.get(category.value) ?? 0;
    if (!category.visible || count <= 0) return [];
    return [{ value: category.value, label: category.label, color: category.color, count, remainder: false, hectares: assignments.areaHa.get(category.value) ?? 0 }];
  }));
  if (legend.includeRemainder) {
    const visible = deriveDisplaySet(state).layerFeatureIds.get(layer.id) ?? new Set(layer.features.map(({ id }) => id));
    const remainder = remainderIds(visible, ids);
    const records = layer.features.filter(({ id }) => remainder.has(id));
    const count = layer.kind === 'master' && state.model
      ? entityViewRows(state.model, state.entityViewLevel, { allowedFragmentIds: remainder }).length
      : records.length;
    const hectares = records.reduce((sum, record) => {
      const geometry = record.feature.getGeometry();
      return sum + (geometry?.getType().includes('Polygon') ? measureArea(geometry).hectares : 0);
    }, 0);
    // Context outside the classified surface: rounded on its own, never part of the classified total.
    if (count) rows.push(...withMetrics(legend, [{ value: '__remainder__', label: legend.remainderLabel, color: state.classificationConfig.remainderColor, count, remainder: true, hectares }]));
  }
  // V1.9.0 E3: grouped legend B → A, from the same final pieces (falls back to the flat legend without B).
  const grouped = layer.kind === 'master' && legend.layout === 'grouped' && legend.showCategories ? groupedLegend(state, legend) : undefined;
  if (grouped) {
    const markInLegend = theme?.scenario && scenarioMarkPlacement(state.scenarios.mark, true) === 'legend';
    return { config: legend, layerName: layer.name, rows: rows.filter(({ remainder }) => remainder), grouped, ...(markInLegend ? { scenario: theme.scenario!.name } : {}) };
  }
  const secondary = layer.kind === 'master' ? secondaryLegend(state, legend) : undefined;
  // The proposal mark lives in the legend unless a badge was chosen (one mark, never two).
  const markInLegend = theme?.scenario && scenarioMarkPlacement(state.scenarios.mark, true) === 'legend';
  return { config: legend, layerName: layer.name, rows, ...(markInLegend ? { scenario: theme.scenario!.name } : {}), ...(secondary ? { secondary } : {}) };
}

/**
 * Grouped legend: every visible B category with area becomes a group of the visible A categories it holds. Shown areas
 * reconcile at every level: groups are apportioned to the grand total, the rows of a group to its shown subtotal.
 */
function groupedLegend(state: AppState, legend: LegendConfig): GroupedLegendComposition | undefined {
  const dual = secondaryTheme(state); const cross = dual ? legendCrossTab(state) : null;
  if (!dual || !cross) return undefined;
  const principal = legendOrdered(state.classificationConfig.categories.filter(({ visible }) => visible), legend.categoryOrder);
  // Every classified hectare has a group: B categories in their order, then the neutral «Sin ‹B›» group.
  const candidates = [
    ...legendOrdered([...dual.categories.values()], legend.secondaryOrder).map((category) => ({ ...category, neutral: false })),
    ...(cross.groups.has(SECONDARY_NO_VALUE) ? [{ value: SECONDARY_NO_VALUE, label: unassignedLabel(dual.config.name), color: UNASSIGNED_COLOR, visible: legend.showUnassigned !== false, neutral: true }] : []),
  ];
  const groups = candidates.filter(({ visible }) => visible).flatMap((category) => {
    const cell = cross.groups.get(category.value); if (!cell) return [];
    const rows = principal.flatMap((style) => { const item = cell.categories.get(style.value); return item ? [{ style, areaHa: item.areaHa, count: item.entities.size }] : []; });
    if (!rows.length) return [];
    const entities = new Set(rows.flatMap(({ style }) => [...cell.categories.get(style.value)!.entities]));
    return [{ category, rows, count: entities.size, areaHa: rows.reduce((sum, row) => sum + row.areaHa, 0), entities }];
  });
  if (!groups.length) return undefined;
  const groupAreas = groups.map(({ areaHa }) => toUnit(legend, areaHa));
  const excludedArea = Math.max(0, toUnit(legend, cross.total.areaHa) - groupAreas.reduce((sum, value) => sum + value, 0));
  const excluded = excludedArea > EPSILON_AREA;
  const shownGroups = apportionRounded(groupAreas, legend.decimals);
  const totalArea = groupAreas.reduce((sum, value) => sum + value, 0); const totalShown = shownGroups.reduce((sum, value) => sum + value, 0);
  const totalCount = new Set(groups.flatMap(({ entities }) => [...entities])).size;
  const subtotals = legend.showSubtotals !== false;
  return {
    title: dual.config.name, line: dual.config.line,
    groups: groups.map((group, index) => {
      const areas = group.rows.map(({ areaHa }) => toUnit(legend, areaHa)); const shown = apportionRounded(areas, legend.decimals, shownGroups[index]!);
      return {
        value: group.category.value, label: group.category.label, color: group.category.color, ...(group.category.neutral ? { neutral: true } : {}), count: group.count, area: groupAreas[index]!, shownArea: shownGroups[index]!,
        metrics: subtotals ? metricsText(legend, group.count, shownGroups[index]!) : '',
        rows: group.rows.map((row, rowIndex) => ({ value: row.style.value, label: row.style.label, color: row.style.color, count: row.count, remainder: false, area: areas[rowIndex]!, shownArea: shown[rowIndex]!, metrics: metricsText(legend, row.count, shown[rowIndex]!) })),
      };
    }),
    ...(legend.showTotal !== false ? { total: { label: excluded ? 'Área mostrada' : 'Total', count: totalCount, area: totalArea, shownArea: Math.round(totalShown * 1e6) / 1e6, metrics: metricsText(legend, totalCount, totalShown) } } : {}),
    excludedArea: excluded ? excludedArea : 0,
  };
}

/** Rows of the secondary classification (same entities as the principal legend, counted per B category). */
function secondaryLegend(state: AppState, legend: LegendConfig): SecondaryLegendComposition | undefined {
  const dual = secondaryTheme(state); if (!dual) return undefined;
  const rows = withMetrics(legend, legendOrdered([...dual.categories.values()], legend.secondaryOrder).flatMap((category) => {
    const count = dual.assignments.counts.get(category.value) ?? 0;
    if (!category.visible || count <= 0) return [];
    return [{ value: category.value, label: category.label, color: category.color, count, remainder: false, neutral: false, hectares: dual.assignments.areaHa.get(category.value) ?? 0 }];
  }).concat(
    // V1.9.0: entities without a B value are listed (neutral row), so A and B add up to the same classified surface.
    legend.showUnassigned !== false && (dual.assignments.counts.get(SECONDARY_NO_VALUE) ?? 0) > 0
      ? [{ value: SECONDARY_NO_VALUE, label: unassignedLabel(dual.config.name), color: UNASSIGNED_COLOR, count: dual.assignments.counts.get(SECONDARY_NO_VALUE)!, remainder: false, neutral: true, hectares: dual.assignments.areaHa.get(SECONDARY_NO_VALUE) ?? 0 }]
      : [],
  ));
  // V1.9.0: no technical note in a management legend (the panel summary keeps the A × B coverage).
  return { title: dual.config.name, rows, line: dual.config.line };
}
